import React, { useState, useMemo, useEffect } from 'react';
import { paymentRecords } from '../../mockData/records';

// Simple icon component to replace lucide-react
const Icon = ({ name, size = 20, className = "" }) => {
  const icons = {
    user: "👤",
    bell: "🔔",
    calendar: "📅",
    dollar: "💰",
    "dollar-sign": "💰",
    book: "📚",
    file: "📄",
    "file-text": "📄",
    users: "👥",
    award: "🏆",
    message: "💬",
    "message-circle": "💬",
    home: "🏠",
    menu: "☰",
    x: "✕",
    search: "🔍",
    chevronDown: "▼",
    star: "⭐",
    clock: "⏰",
    mapPin: "📍",
    phone: "📞",
    mail: "📧",
    alert: "⚠️",
    filter: "🔽",
    pin: "📌",
    download: "⬇️",
    paperclip: "📎",
    send: "➤",
    plus: "➕",
    check: "✓",
    triangle: "⚠️",
    card: "💳",
    wifi: "📶",
    monitor: "💻",
    tablet: "📱",
    smartphone: "📱",
    camera: "📷",
    headphones: "🎧",
    megaphone: "📢",
    info: "ℹ️",
    settings: "⚙️",
    "help-circle": "❓",
    shield: "🛡️",
    "calendar-days": "🗓️"
  };
  
  return (
    <span 
      className={className}
      style={{ 
        fontSize: `${size}px`, 
        display: 'inline-block',
        lineHeight: 1
      }}
    >
      {icons[name] || "?"}
    </span>
  );
};

// Payment Records Component
const PaymentRecordsModule = ({ paymentData, onBack, formatCurrency, calculateTotalAmount, formatDate, onTabChange }) => {
  const [navigationSource, setNavigationSource] = useState(null);

  useEffect(() => {
    // Check if user came from class dashboard
    const source = localStorage.getItem('navigationSource');
    if (source) {
      try {
        const sourceInfo = JSON.parse(source);
        setNavigationSource(sourceInfo);
      } catch (e) {
        console.error('Error parsing navigation source:', e);
      }
    }
  }, []);

  const handleBackNavigation = () => {
    if (navigationSource?.source === 'class-dashboard' && navigationSource?.returnTo === 'teacher-directory') {
      // Go back to teacher directory (which shows class dashboard)
      // Use the onTabChange function to navigate to teacher-directory tab
      onTabChange('teacher-directory');
      // Clear the navigation source
      localStorage.removeItem('navigationSource');
    } else {
      // Default back behavior
      onBack();
    }
  };

  const [filter, setFilter] = useState('全部');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('dueDate');

  // Filter and sort payment records
  const filteredPayments = paymentRecords
    .filter(payment => {
      if (filter === '全部') return true;
      return payment.status === filter;
    })
    .filter(payment => {
      if (!searchTerm) return true;
      return payment.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
             payment.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
             payment.category.toLowerCase().includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'amount':
          return b.amount - a.amount;
        case 'dueDate':
          return new Date(a.dueDate) - new Date(b.dueDate);
        case 'status':
          return a.status.localeCompare(b.status);
        default:
          return 0;
      }
    });

  // Get status color and text
  const getStatusInfo = (status) => {
    switch (status) {
      case 'paid':
        return { color: '#166534', bgColor: '#dcfce7', text: '已繳費' };
      case 'overdue':
        return { color: '#991b1b', bgColor: '#fee2e2', text: '逾期' };
      case 'pending':
        return { color: '#92400e', bgColor: '#fef3c7', text: '待繳費' };
      default:
        return { color: '#6b7280', bgColor: '#f3f4f6', text: status };
    }
  };

  // Get total amounts by status
  const getTotals = () => {
    const totals = filteredPayments.reduce((acc, payment) => {
      if (!acc[payment.status]) acc[payment.status] = 0;
      acc[payment.status] += payment.amount;
      return acc;
    }, {});
    
    return totals;
  };

  const totals = getTotals();

  return (
    <div style={{minHeight: '100vh', backgroundColor: '#f3f4f6'}}>
      {/* Header with return button */}
      <div className="module-header">
        <button className="back-button" onClick={handleBackNavigation}>
          {navigationSource?.source === 'class-dashboard' ? '← 返回課室管理' : '← 返回主頁'}
        </button>
        <h1>繳費紀錄</h1>
      </div>

      {/* Summary Cards */}
      <div style={{padding: '16px'}}>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px'}}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            borderLeft: '4px solid #10b981'
          }}>
            <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
              <Icon name="dollar" size={24} />
              <div>
                <p style={{fontSize: '14px', color: '#6b7280', margin: 0}}>總計</p>
                <p style={{fontSize: '24px', fontWeight: '700', color: '#1f2937', margin: 0}}>
                  HK${filteredPayments.reduce((sum, p) => sum + p.amount, 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
          
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            borderLeft: '4px solid #059669'
          }}>
            <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
              <Icon name="check" size={24} />
              <div>
                <p style={{fontSize: '14px', color: '#6b7280', margin: 0}}>已繳費</p>
                <p style={{fontSize: '24px', fontWeight: '700', color: '#059669', margin: 0}}>
                  HK${(totals.paid || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
          
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            borderLeft: '4px solid #f59e0b'
          }}>
            <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
              <Icon name="clock" size={24} />
              <div>
                <p style={{fontSize: '14px', color: '#6b7280', margin: 0}}>待繳費</p>
                <p style={{fontSize: '24px', fontWeight: '700', color: '#f59e0b', margin: 0}}>
                  HK${(totals.pending || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
          
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            borderLeft: '4px solid #dc2626'
          }}>
            <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
              <Icon name="alert" size={24} />
              <div>
                <p style={{fontSize: '14px', color: '#6b7280', margin: 0}}>逾期</p>
                <p style={{fontSize: '24px', fontWeight: '700', color: '#dc2626', margin: 0}}>
                  HK${(totals.overdue || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center'}}>
            {/* Status Filter */}
            <div style={{display: 'flex', gap: '8px'}}>
              {['全部', 'paid', 'pending', 'overdue'].map(status => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    backgroundColor: filter === status ? '#10b981' : '#f3f4f6',
                    color: filter === status ? 'white' : '#6b7280',
                    transition: 'all 0.2s'
                  }}
                >
                  {status === 'paid' ? '已繳費' : status === 'pending' ? '待繳費' : status === 'overdue' ? '逾期' : status}
                </button>
              ))}
            </div>

            {/* Search */}
            <div style={{flex: 1, minWidth: '200px'}}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#f3f4f6',
                borderRadius: '8px',
                padding: '8px 12px',
                gap: '8px'
              }}>
                <Icon name="search" size={16} />
                <input 
                  type="text"
                  placeholder="搜尋繳費項目..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    border: 'none',
                    backgroundColor: 'transparent',
                    outline: 'none',
                    flex: 1,
                    fontSize: '14px',
                    color: '#6b7280'
                  }}
                />
              </div>
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="dueDate">按到期日排序</option>
              <option value="amount">按金額排序</option>
              <option value="status">按狀態排序</option>
            </select>
          </div>
        </div>

        {/* Payment Records List */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '20px'}}>
            繳費項目 ({filteredPayments.length})
          </h2>
          
          {filteredPayments.length === 0 ? (
            <div style={{textAlign: 'center', padding: '40px', color: '#6b7280'}}>
              <Icon name="info" size={48} />
              <p style={{margin: '16px 0 0 0'}}>沒有找到符合條件的繳費項目</p>
            </div>
          ) : (
            <div style={{display: 'grid', gap: '16px'}}>
              {filteredPayments.map(payment => {
                const statusInfo = getStatusInfo(payment.status);
                return (
                  <div key={payment.id} style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '20px',
                    backgroundColor: payment.status === 'paid' ? '#f0fdf4' : 
                                   payment.status === 'overdue' ? '#fef2f2' : '#fefce8',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                  >
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px'}}>
                      <div style={{flex: 1}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px'}}>
                          <h3 style={{fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: 0}}>
                            {payment.type}
                          </h3>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            backgroundColor: statusInfo.bgColor,
                            color: statusInfo.color,
                            fontWeight: '500'
                          }}>
                            {statusInfo.text}
                          </span>
                        </div>
                        <p style={{color: '#6b7280', fontSize: '14px', margin: '0 0 8px 0', lineHeight: '1.5'}}>
                          {payment.description}
                        </p>
                        <div style={{display: 'flex', gap: '16px', fontSize: '12px', color: '#6b7280'}}>
                          <span>學生: {payment.studentName} ({payment.studentId})</span>
                          <span>類別: {payment.category}</span>
                        </div>
                      </div>
                      
                      <div style={{textAlign: 'right', marginLeft: '20px'}}>
                        <p style={{fontSize: '24px', fontWeight: '700', color: '#1f2937', margin: '0 0 8px 0'}}>
                          HK${payment.amount.toLocaleString()}
                        </p>
                        {payment.receiptNumber && (
                          <p style={{fontSize: '12px', color: '#059669', margin: '0 0 4px 0'}}>
                            收據: {payment.receiptNumber}
                          </p>
                        )}
                        {payment.method && (
                          <p style={{fontSize: '12px', color: '#6b7280', margin: 0}}>
                            付款方式: {payment.method}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px',
                      backgroundColor: 'rgba(255, 255, 255, 0.5)',
                      borderRadius: '8px',
                      border: '1px solid rgba(0, 0, 0, 0.05)'
                    }}>
                      <div style={{display: 'flex', gap: '16px', fontSize: '12px', color: '#6b7280'}}>
                        <span>到期日: {payment.dueDate}</span>
                        {payment.paymentDate && <span>繳費日: {payment.paymentDate}</span>}
                      </div>
                      
                      {payment.status === 'pending' && (
                        <button style={{
                          padding: '8px 16px',
                          backgroundColor: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                        >
                          立即繳費
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentRecordsModule;
